import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../types';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';

interface TeamSubscriptionData {
  organization_subscription: any;
  team_usage: {
    current_member_count: number;
    storage_used_bytes: number;
    content_count: number;
  };
}

export function useTeamSubscription(teamId: string, organizationId?: string) {
  const [subscriptionData, setSubscriptionData] = useState<TeamSubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchSubscription = useCallback(async () => {
    if (!teamId || !organizationId) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log(`Fetching team and subscription data for org: ${organizationId}, team: ${teamId}`);

      // First, get the team details to find its subscriptionId
      const team = await teamService.getTeam(teamId);
      const teamSubscriptionId = team.subscriptionId;

      console.log(`Team ${teamId} has subscriptionId: ${teamSubscriptionId}`);

      if (!teamSubscriptionId) {
        // Team has no subscription assigned
        setSubscriptionData(null);
        return;
      }

      // Get organization subscriptions and find the one assigned to this team
      const orgSubscriptions = await organizationService.getOrganizationSubscriptions(organizationId);

      // Find subscription by matching the ID
      const teamSubscription = orgSubscriptions.find((sub: any) =>
        sub.id === teamSubscriptionId || sub._id === teamSubscriptionId
      );

      console.log(`Found subscription for team:`, teamSubscription);

      if (teamSubscription) {
        // Use actual team data for usage metrics - keep bytes as source of truth
        setSubscriptionData({
          organization_subscription: teamSubscription,
          team_usage: {
            current_member_count: team.memberCount || 0,
            storage_used_bytes: team.storageUsedBytes || 0,
            content_count: team.contentCount || 0
          }
        });
      } else {
        // Subscription ID exists but subscription not found in org subscriptions
        console.warn(`Team has subscriptionId ${teamSubscriptionId} but subscription not found in org subscriptions`);
        setSubscriptionData(null);
      }
    } catch (err) {
      console.error('Error fetching team subscription:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [teamId, organizationId, refreshKey]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Refresh function that forces a re-fetch by incrementing the key
  const refreshSubscription = useCallback(async () => {
    console.log('Forcing subscription refresh by incrementing key');
    setRefreshKey(prev => prev + 1);
  }, []);

  return {
    subscriptionData,
    subscription: subscriptionData?.organization_subscription,
    usage: subscriptionData?.team_usage,
    isLoading,
    error,
    refreshSubscription
  };
}

export default useTeamSubscription;